import { parseSecondsToString } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import { CSSProperties, useEffect, useState } from 'react';
import styled from 'styled-components';
import { DistanceIcon } from '../../../../assets/svg/DistanceIcon';
import { TimeClockIcon } from '../../../../assets/svg/TimeClockIcon';
import { kmToSM } from '../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../react-components/Text';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanOptimization } from '../hooks/plan-details/useRetrieveActivePlanOptimization';

const SnackbarMessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${RoutalPalette.secondary40};
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
`;

export const OptimizationSnackbar = () => {
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: project } = useRetrieveActiveProject();
  const { data: optimization } = useRetrieveActivePlanOptimization();

  const [opacity, setOpacity] = useState<CSSProperties[`opacity`]>(0);
  const [transition, setTransition] = useState<CSSProperties[`transition`]>(`none`);
  const [display, setDisplay] = useState<CSSProperties[`display`]>(`none`);
  const [time, setTime] = useState<number>(0);
  const [distance, setDistance] = useState<number>(0);
  const [previousOptimizationState, setPreviousOptimizationState] = useState<string>(``);

  // Update time and distance values when drivers change
  useEffect(() => {
    if (drivers) {
      const totalDistance = drivers!.reduce((acc, driver) => acc + driver.getTotalDistanceKM(), 0);
      setDistance(totalDistance);

      const totalTime = drivers!.reduce((acc, driver) => acc + driver.getTotalMinutes(), 0);
      setTime(totalTime);
    }
  }, [drivers]);

  // Manages the timeouts for the snackbar
  useEffect(() => {
    if (!optimization) return;

    if (optimization.status === `finished` && previousOptimizationState === `running`) {
      setOpacity(1);
      setDisplay(`flex`);
      setTimeout(() => {
        setTransition(`opacity 1s ease-in-out`);
      }, 3000);

      setTimeout(() => {
        setOpacity(0);
      }, 5000);
      setTimeout(() => {
        setTransition(`none`);
        setDisplay(`none`);
      }, 6000);
    }

    if (optimization.status && optimization.status !== previousOptimizationState) {
      setPreviousOptimizationState(optimization.status);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimization]);

  if (!activePlan || !optimization || !drivers || !project) {
    return null;
  }

  return (
    <div
      onClick={() => {
        setOpacity(0);
        setTransition(`none`);
        setDisplay(`none`);
      }}
      style={{
        opacity: opacity,
        transition: transition,
        display: display,
        position: `absolute`,
        left: `50vw`,
        bottom: `30px`,
        justifyContent: `center`,
        zIndex: `9999`,
      }}
    >
      <SnackbarMessageContainer>
        <TimeClockIcon />
        <HSpacer small />
        <Text
          intlId="planner.plan_details.optimizing.snackbar.time"
          intlValues={{ value: parseSecondsToString(time * 60) }}
          style={{ color: RoutalPalette.neutral00 }}
        />

        <HSpacer medium />

        <DistanceIcon />
        <HSpacer small />
        <Text
          intlId="planner.plan_details.optimizing.snackbar.distance"
          intlValues={{
            value: `${kmToSM(distance, project.units?.distance).toFixed(1)}${project.units?.distance}`,
          }}
          style={{ color: RoutalPalette.neutral00 }}
        />
      </SnackbarMessageContainer>
    </div>
  );
};
